(() => {
  if (window.__gbLatestProductsScriptLoaded) return;
  window.__gbLatestProductsScriptLoaded = true;

  const SECTION_SELECTOR = '[data-latest-products-section]';
  const TRACK_SELECTOR = '[data-latest-track]';
  const ARROW_SELECTOR = '[data-latest-arrow]';
  const QUICK_ADD_SELECTOR = '[data-quick-add]';
  const MOBILE_QUERY = window.matchMedia('(max-width: 749px)');
  const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SUCCESS_DURATION = 1700;
  const ERROR_DURATION = 700;
  const instances = new WeakMap();

  class LatestProductsSection {
    constructor(section) {
      this.section = section;
      this.track = section.querySelector(TRACK_SELECTOR);
      this.arrow = section.querySelector(ARROW_SELECTOR);
      this.mobileSwipeEnabled = section.dataset.mobileSwipe === 'true';

      this.onScroll = this.updateArrowState.bind(this);
      this.onResize = this.updateArrowState.bind(this);

      if (this.track && this.mobileSwipeEnabled) {
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize);

        if (typeof MOBILE_QUERY.addEventListener === 'function') {
          MOBILE_QUERY.addEventListener('change', this.onResize);
        } else if (typeof MOBILE_QUERY.addListener === 'function') {
          MOBILE_QUERY.addListener(this.onResize);
        }

        this.updateArrowState();
      }
    }

    destroy() {
      if (this.track) {
        this.track.removeEventListener('scroll', this.onScroll);
      }

      window.removeEventListener('resize', this.onResize);

      if (typeof MOBILE_QUERY.removeEventListener === 'function') {
        MOBILE_QUERY.removeEventListener('change', this.onResize);
      } else if (typeof MOBILE_QUERY.removeListener === 'function') {
        MOBILE_QUERY.removeListener(this.onResize);
      }
    }

    updateArrowState() {
      if (!this.arrow || !this.track) return;

      if (!this.mobileSwipeEnabled || !MOBILE_QUERY.matches) {
        this.arrow.classList.add('is-hidden');
        return;
      }

      const canScroll = this.track.scrollWidth - this.track.clientWidth > 6;
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 6;

      this.arrow.classList.toggle('is-hidden', !canScroll || atEnd);
    }
  }

  function getSectionsWithin(root) {
    const sections = [];

    if (root instanceof Element && root.matches(SECTION_SELECTOR)) {
      sections.push(root);
    }

    if (root instanceof Element || root instanceof Document) {
      sections.push(...root.querySelectorAll(SECTION_SELECTOR));
    }

    return sections;
  }

  function initSections(root = document) {
    getSectionsWithin(root).forEach((section) => {
      if (instances.has(section)) return;
      instances.set(section, new LatestProductsSection(section));
    });
  }

  function destroySections(root) {
    getSectionsWithin(root).forEach((section) => {
      const instance = instances.get(section);
      if (!instance) return;
      instance.destroy();
      instances.delete(section);
    });
  }

  function getCartTarget() {
    const selectors = ['[data-cart-icon]', 'a[href="/cart"]', '.header__icon--cart', '.site-header__cart', 'cart-icon'];

    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (!(element instanceof HTMLElement)) continue;

      const rect = element.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        return element;
      }
    }

    return null;
  }

  async function addToCartSilently(variantId) {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok || data.status) {
      const message = data.description || data.message || 'Unable to add product to cart.';
      throw new Error(message);
    }

    return data;
  }

  async function fetchCartState() {
    const response = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Unable to fetch cart.');
    }

    return response.json();
  }

  function updateCartCountUI(cart) {
    const itemCount = Number(cart?.item_count || 0);
    const countText = String(itemCount);

    for (const countElement of document.querySelectorAll('[data-cart-count]')) {
      countElement.textContent = countText;
    }

    for (const bubbleCount of document.querySelectorAll('[ref="cartBubbleCount"], .cart-bubble__count')) {
      bubbleCount.textContent = countText;
    }

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: countText,
        timestamp: Date.now(),
      })
    );
  }

  function dispatchSilentCartEvents(cart, button) {
    // Intentionally no global cart events to avoid opening cart drawer.
    // Count bubble is updated via updateCartCountUI().
    void cart;
    void button;
  }

  function animateFlyToCart(productImage, cartTarget) {
    if (!(productImage instanceof HTMLImageElement) || !(cartTarget instanceof HTMLElement)) {
      return Promise.resolve();
    }

    if (REDUCED_MOTION_QUERY.matches) {
      return Promise.resolve();
    }

    const imageRect = productImage.getBoundingClientRect();
    const targetRect = cartTarget.getBoundingClientRect();

    if (imageRect.width <= 0 || imageRect.height <= 0 || targetRect.width <= 0 || targetRect.height <= 0) {
      return Promise.resolve();
    }

    const src = productImage.currentSrc || productImage.src;
    if (!src) return Promise.resolve();

    const size = Math.max(44, Math.min(64, Math.min(imageRect.width, imageRect.height)));
    const targetScale = 0.2;

    const startLeft = imageRect.left + (imageRect.width - size) / 2;
    const startTop = imageRect.top + (imageRect.height - size) / 2;

    const targetSize = size * targetScale;
    const endLeft = targetRect.left + (targetRect.width - targetSize) / 2;
    const endTop = targetRect.top + (targetRect.height - targetSize) / 2;

    const dx = endLeft - startLeft;
    const dy = endTop - startTop;
    const distance = Math.hypot(dx, dy);
    const arcLift = Math.max(36, Math.min(120, distance * 0.22));
    const midX = dx * 0.58;
    const midY = dy * 0.52 - arcLift;

    const flyingImage = document.createElement('div');
    flyingImage.className = 'gb-latest-products__fly-image';
    flyingImage.style.left = `${startLeft}px`;
    flyingImage.style.top = `${startTop}px`;
    flyingImage.style.width = `${size}px`;
    flyingImage.style.height = `${size}px`;
    flyingImage.style.transformOrigin = 'center center';

    const imageEl = document.createElement('img');
    imageEl.src = src;
    imageEl.alt = '';
    imageEl.loading = 'eager';
    flyingImage.appendChild(imageEl);

    document.body.appendChild(flyingImage);

    return new Promise((resolve) => {
      const keyframes = [
        {
          transform: 'translate3d(0, 0, 0) scale(1)',
          opacity: 1,
          offset: 0,
        },
        {
          transform: `translate3d(${midX}px, ${midY}px, 0) scale(0.62)`,
          opacity: 0.96,
          offset: 0.56,
        },
        {
          transform: `translate3d(${dx}px, ${dy}px, 0) scale(${targetScale})`,
          opacity: 0.06,
          offset: 1,
        },
      ];

      const timing = {
        duration: 760,
        easing: 'cubic-bezier(0.22, 0.75, 0.2, 1)',
        fill: 'forwards',
      };

      requestAnimationFrame(() => {
        if (typeof flyingImage.animate === 'function') {
          const animation = flyingImage.animate(keyframes, timing);
          animation.onfinish = () => {
            flyingImage.remove();
            resolve();
          };
          animation.oncancel = () => {
            flyingImage.remove();
            resolve();
          };
        } else {
          window.setTimeout(() => {
            flyingImage.remove();
            resolve();
          }, timing.duration);
        }
      });
    });
  }

  function setButtonLoading(button, isLoading) {
    button.classList.toggle('is-loading', isLoading);
    button.disabled = isLoading;
  }

  function showSuccessState(button) {
    const defaultLabel = button.dataset.defaultAriaLabel || 'Add to cart';

    button.classList.remove('is-loading', 'is-error');
    button.classList.add('is-success');
    button.disabled = true;
    button.setAttribute('aria-label', 'Added');

    window.setTimeout(() => {
      button.classList.remove('is-success');
      button.disabled = false;
      button.setAttribute('aria-label', defaultLabel);
    }, SUCCESS_DURATION);
  }

  function showErrorState(button) {
    const defaultLabel = button.dataset.defaultAriaLabel || 'Add to cart';

    button.classList.remove('is-loading', 'is-success');
    button.classList.add('is-error');
    button.disabled = true;
    button.setAttribute('aria-label', 'Error');

    window.setTimeout(() => {
      button.classList.remove('is-error');
      button.disabled = false;
      button.setAttribute('aria-label', defaultLabel);
    }, ERROR_DURATION);
  }

  async function onQuickAddClick(event) {
    const button = event.target.closest(QUICK_ADD_SELECTOR);
    if (!(button instanceof HTMLButtonElement)) return;

    event.preventDefault();

    if (button.disabled || button.classList.contains('is-loading') || button.classList.contains('is-success')) {
      return;
    }

    if (!button.dataset.defaultAriaLabel) {
      button.dataset.defaultAriaLabel = button.getAttribute('aria-label') || 'Add to cart';
    }

    const variantId = button.dataset.variantId;
    if (!variantId) {
      showErrorState(button);
      return;
    }

    setButtonLoading(button, true);

    const productCard = button.closest('[data-product-card]');
    const productImage = productCard?.querySelector('[data-product-image]') || null;
    const cartTarget = getCartTarget();

    try {
      await addToCartSilently(variantId);

      const flyAnimation =
        productImage instanceof HTMLImageElement && cartTarget instanceof HTMLElement
          ? animateFlyToCart(productImage, cartTarget)
          : Promise.resolve();

      showSuccessState(button);

      fetchCartState()
        .then((cart) => {
          updateCartCountUI(cart);
          dispatchSilentCartEvents(cart, button);
        })
        .catch((error) => {
          console.error('[latest-products] cart refresh failed', error);
        });

      flyAnimation.catch(() => {});
    } catch (error) {
      console.error('[latest-products] quick add failed', error);
      showErrorState(button);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSections(document), { once: true });
  } else {
    initSections(document);
  }

  document.addEventListener('click', onQuickAddClick);

  document.addEventListener('shopify:section:load', (event) => {
    if (!(event.target instanceof Element)) return;
    initSections(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (!(event.target instanceof Element)) return;
    destroySections(event.target);
  });
})();
